import { Inject, Injectable } from '@angular/core';
import { catchError, Observable, of, filter, switchMap, take, startWith } from 'rxjs';
import { PLACEHOLDER_IMAGE } from '@core/utils/utils';
import { mergeMap, delay} from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@env/environment';
import { AUTH_SERVICE } from '@core/auth/token';
import { AuthServiceInterface } from '@core/auth/auth.interface';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  constructor(
    private http: HttpClient,
    @Inject(AUTH_SERVICE) private authService: AuthServiceInterface
  ) {}

  public getProfileDocument(filename?: string): Observable<string> {
    if (!filename) {
      return of(PLACEHOLDER_IMAGE);
    }

    const url = `${environment.profileUrl}/${filename}`;
    return this.http.get(url, { responseType: 'blob' }).pipe(
      switchMap(blob => this.readDataFromURL(blob)),
      catchError(error => {
        console.error(`[ProfileService] Failed to fetch or process profile from ${url}`, error);
        return of(PLACEHOLDER_IMAGE);
      })
    );
  }

  /**
   * @param response The response coming from the backend
   * @returns The base64 encoded image
   */
  private readDataFromURL = (response: unknown): Observable<string> => {
    return new Observable<string>((subscriber) => {
      const reader = new FileReader();
      reader.readAsDataURL(response as Blob);
      reader.onloadend = () => {
        subscriber.next((reader.result as string).replace('data:application/json;base64,', 'data:image/jpeg;base64,'));
      };
    });
  };

  /**
   * Downloads the profile as a file (PDF/DOCX).
   * Returns a Blob instead of a Base64 string to handle binary file types correctly.
   */
  public downloadProfile(email: string, params: any): Observable<Blob> {
    // Convert the params object to a query string
    // Note: We handle the casting to string for URLSearchParams safety
    const safeParams: Record<string, string> = {};
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        safeParams[key] = String(params[key]);
      }
    });

    const queryString = new URLSearchParams(safeParams).toString();
    const url = `${environment.profileUrl}/${encodeURIComponent(email)}?${queryString}`;

    // Request the data as a Blob (Binary Large Object)
    return this.http.get(url, { responseType: 'blob' });
  }

}
